import { useParams, Navigate } from 'react-router-dom';
import CityHub from './CityHub';
import SectorService from './SectorService';
import CentreDetail from '@/pages/CentreDetail';
import { CITY_SLUGS } from '@/data/cities';
import { SECTORS } from '@/data/sectors';
import { CITY_PREFIX, sectorPath } from '@/utils/seoPaths';
import { parseCityAndSector } from '@/utils/slug';

/**
 * Every programmatic URL funnels through here because React Router only
 * matches a `:param` against a whole segment - `/physiotherapy-in-:city`
 * silently doesn't match.
 *
 * Shapes handled:
 *   physiotherapy-in-noida                       → CityHub
 *   physiotherapy-in-noida/sector-18             → SectorService (sub-region)
 *   physiotherapy-in-noida/sector-18/<centreId>  → CentreDetail
 *   back-pain-therapy-in-noida[-sector-18]       → SectorService (service page)
 *   physiotherapy-in-noida-sector-18             → 301-style redirect to the nested form
 *
 * Anything else falls through to the clinic directory.
 */
export default function SeoDispatcher() {
  const { slug, subSlug, centreId } = useParams<{
    slug: string;
    subSlug: string;
    centreId: string;
  }>();

  if (!slug) return <Navigate to="/centres" replace />;

  const isCityShape = slug.startsWith(CITY_PREFIX);

  // ---- Nested shapes: /physiotherapy-in-<city>/<sub-region>[/<centre-id>] ----
  if (subSlug) {
    if (!isCityShape) return <Navigate to="/centres" replace />;
    const citySlug = slug.slice(CITY_PREFIX.length);
    const known =
      CITY_SLUGS.includes(citySlug) &&
      SECTORS.some((s) => s.citySlug === citySlug && s.slug === subSlug);
    if (!known) return <Navigate to="/centres" replace />;
    if (centreId) return <CentreDetail />;
    return <SectorService hubOnly />;
  }

  // ---- Single-segment shapes ----
  if (isCityShape) {
    const rest = slug.slice(CITY_PREFIX.length);
    // Legacy flat sub-region URL - send it to the nested canonical.
    const parsed = parseCityAndSector(rest, CITY_SLUGS, SECTORS);
    if (parsed.citySlug && parsed.sectorSlug) {
      return <Navigate to={sectorPath(parsed.citySlug, parsed.sectorSlug)} replace />;
    }
    return <CityHub />;
  }

  if (slug.includes('-in-')) return <SectorService />;

  return <Navigate to="/centres" replace />;
}
